import * as React from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { StatusChip, type StatusChipTone } from '@/components/ui/status-chip';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { KpiSourcePill } from '@/components/ui/kpi-source-pill';
import { ChartLegend, UNIT_SWATCH, UnitSquares } from '@/components/ui/charts';
import {
  RATING_LABEL,
  RATING_UNIT_TONE,
  ratingUnit,
  type RatingValue,
} from '@/components/ui/rating-chip';
import { ApiError } from '@/lib/api/client';
import { useEngagementRequirementsQuery } from '@/lib/api/engagement-requirements';
import {
  useVendorIntelligenceQuery,
  type Disposition,
  type ProductInScope,
  type VendorIntelligence,
} from '@/lib/api/vendor-intelligence';
import {
  headerSummary,
  headerSummaryText,
} from '@/components/engagement-requirements/requirements-logic';
import {
  DISPOSITION_LABEL,
  VI_INDEX_PATH,
  dispositionRows,
} from '@/components/vendor-intelligence/vi-logic';
import { requirementSourceCounts } from './module-status-logic';
import { OVERVIEW_LINK } from './overview-go';

/**
 * Selection on the engagement Overview, drawn (5 Nov 2026): the
 * requirements as one bar split by criticality, and each vendor in scope
 * as a row of squares, one per client area, coloured by the area's rating
 * rolled up by the server from the confirmed module ratings (dashed while
 * none is confirmed), with its shortlist disposition.
 *
 * The requirement figures are counted over the full list the Selection
 * screen holds (one response, no paging), as its own header does; the
 * vendor figures are the evaluation's, untouched. Shown only when the
 * engagement's Selection module is on.
 */
export function SelectionStatusCard({
  engagementId,
  discoveryOn,
}: {
  engagementId: string;
  /** Discovery is on too: say how many requirements came from it. */
  discoveryOn: boolean;
}) {
  const requirements = useEngagementRequirementsQuery(engagementId);
  const params = { engagementId };

  const rows = requirements.data?.items ?? [];
  const live = rows.filter((r) => r.status !== 'removed');
  const counts = requirementSourceCounts(rows);

  return (
    <section
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      aria-labelledby="selection-status-title"
      data-testid="selection-status-card"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 id="selection-status-title" className="t-h3">
            Selection
          </h2>
          <p className="t-meta">
            {requirements.data
              ? headerSummaryText(headerSummary(live))
              : 'The client’s requirements and the vendors measured against them'}
          </p>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/engagements/$engagementId/selection" params={params}>
            Open Selection
            <ChevronRight className="size-3" aria-hidden="true" />
          </Link>
        </Button>
      </div>

      {requirements.error ? (
        requirements.error instanceof ApiError && requirements.error.code === 'not_found' ? (
          <p className="text-body-sm text-ink-500">
            Selection is not available here: the module is off for this engagement, or you do not
            have access to it.
          </p>
        ) : (
          <ErrorMessage error={requirements.error} />
        )
      ) : !requirements.data ? (
        <p className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading requirements…
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <div className={BLOCK_HEAD}>
              <h3 className={BLOCK_TITLE}>Requirements</h3>
              <Link
                to="/engagements/$engagementId/selection"
                params={params}
                className={OVERVIEW_LINK}
              >
                Requirements
              </Link>
            </div>
            {counts.total === 0 ? (
              <p className="text-body-sm text-ink-500">
                None yet. Load them from the library, import a spreadsheet or write one by hand.
              </p>
            ) : (
              <>
                <div
                  role="img"
                  aria-label={`${counts.critical} critical, ${counts.desired} desired, ${counts.notSet} not set`}
                  className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-ink-100"
                >
                  <span className="bg-ink-900" style={{ flexGrow: counts.critical }} />
                  <span className="bg-ink-300" style={{ flexGrow: counts.desired }} />
                  <span className="bg-accent-amber-600" style={{ flexGrow: counts.notSet }} />
                </div>
                <ChartLegend
                  className="mt-2.5"
                  items={[
                    { color: 'bg-ink-900', label: <Count n={counts.critical}>Critical</Count> },
                    { color: 'bg-ink-300', label: <Count n={counts.desired}>Desired</Count> },
                    ...(counts.notSet > 0
                      ? [
                          {
                            color: 'bg-accent-amber-600',
                            label: <Count n={counts.notSet}>Not set</Count>,
                          },
                        ]
                      : []),
                  ]}
                >
                  {discoveryOn ? (
                    <span className="text-meta text-ink-700">
                      <Count n={counts.discovery}>From Discovery</Count>
                    </span>
                  ) : null}
                </ChartLegend>
              </>
            )}
          </div>

          <VendorFit engagementId={engagementId} />

          <KpiSourcePill
            source="From the requirement list and confirmed ratings"
            className="self-start"
          />
        </div>
      )}
    </section>
  );
}

const BLOCK_HEAD = 'mb-2 flex items-baseline justify-between gap-3';
const BLOCK_TITLE = 'text-body-sm font-semibold text-ink-900';

function VendorFit({ engagementId }: { engagementId: string }) {
  const q = useVendorIntelligenceQuery(engagementId);
  return (
    <div className="border-t border-ink-100 pt-4">
      <div className={BLOCK_HEAD}>
        <h3 className={BLOCK_TITLE}>Vendor fit</h3>
        <Link to={VI_INDEX_PATH} params={{ engagementId }} className={OVERVIEW_LINK}>
          Vendor intelligence
        </Link>
      </div>
      {q.error ? (
        <p className="text-body-sm text-ink-500">Vendor fit is not available right now.</p>
      ) : !q.data ? (
        <p className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading vendors…
        </p>
      ) : q.data.products.length === 0 ? (
        <p className="text-body-sm text-ink-500">
          No vendors in scope yet. Ratings start once two are added.
        </p>
      ) : (
        <FitRows e={q.data} />
      )}
    </div>
  );
}

function FitRows({ e }: { e: VendorIntelligence }) {
  const rows = dispositionRows(e);
  const seen = new Set<RatingValue | 'pending'>();
  const cells = rows.map(({ product }) =>
    e.areas.map((a) => {
      const cell = a.products.find((p) => p.product_id === product.product_id);
      const rating =
        cell && cell.modules.some((m) => m.rating !== null) ? (cell.rating as RatingValue) : null;
      seen.add(rating ?? 'pending');
      return ratingUnit(
        `${product.product_id}-${a.key}`,
        rating,
        `${a.name}: ${rating ? RATING_LABEL[rating] : 'not confirmed yet'}`,
      );
    }),
  );
  const legend = (['strong', 'partial', 'gap', 'unknown', 'pending'] as const).filter((k) =>
    seen.has(k),
  );

  if (e.areas.length === 0) {
    return (
      <p className="text-body-sm text-ink-500">
        No client area has a requirement yet, so there is nothing to rate by area.
      </p>
    );
  }
  return (
    <>
      <p className="t-meta mb-1">One square per client area</p>
      <ul className="m-0 list-none p-0">
        {rows.map(({ product }, i) => (
          <li
            key={product.product_id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-ink-100 py-2"
          >
            <span
              className="w-36 flex-none truncate text-body-sm font-semibold text-ink-900"
              title={
                product.vendor.name === product.name
                  ? product.name
                  : `${product.vendor.name} · ${product.name}`
              }
            >
              {product.name}
            </span>
            <UnitSquares
              className="min-w-0 flex-1"
              units={cells[i]!}
              label={`${product.name} by client area`}
            />
            <DispositionChip product={product} />
          </li>
        ))}
      </ul>
      <ChartLegend
        className="mt-2.5"
        items={legend.map((k) => ({
          color: UNIT_SWATCH[RATING_UNIT_TONE[k]],
          label: k === 'pending' ? 'Not confirmed yet' : RATING_LABEL[k],
        }))}
      />
    </>
  );
}

const DISPOSITION_TONE: Record<Disposition, StatusChipTone> = {
  shortlisted: 'healthy',
  alternate: 'neutral',
  set_aside: 'unassessed',
  not_assessed: 'unset',
};

function DispositionChip({ product }: { product: ProductInScope }) {
  if (!product.disposition) return null;
  const confirmed = product.disposition_status === 'confirmed';
  const label = DISPOSITION_LABEL[product.disposition];
  return (
    <StatusChip
      tone={confirmed ? DISPOSITION_TONE[product.disposition] : 'unset'}
      size="sm"
      title={confirmed ? `${label}, confirmed` : `${label}, proposed by code from the fit`}
    >
      {confirmed ? label : `${label}, proposed`}
    </StatusChip>
  );
}

function Count({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <span>
      {children}{' '}
      <b className="font-semibold tabular-nums text-ink-900">{n.toLocaleString('en-US')}</b>
    </span>
  );
}
